/**
 * Single source of truth for daily macro goals (calories / protein / carbs /
 * fat / fiber).
 *
 * Bug history: the consumer Nutrition page and the trainer's view of the
 * same client used to compute these independently:
 *   • Consumer: split based on weight goal (lose → 40/30/30, gain → 30/45/25,
 *     maintain → 30/40/30); fiber = age + gender RDA (man <50 → 38, etc.);
 *     calorie denominator = baseTdee + adjustment only.
 *   • Trainer view: always 30/40/30; fiber hardcoded to 25; calorie
 *     denominator INCLUDED burned cals + step cals.
 * Result: a male client < 50 with weight goal "lose" saw fiber 38 in the app
 * and fiber 25 in his trainer's dashboard, plus mismatched protein/carb/fat
 * goals. This file unifies both calls so they ALWAYS produce identical numbers.
 *
 * Inputs come either from localStorage (consumer / personal mode) or from the
 * `body_info` blob the trainer's snapshot API returns. We deliberately do NOT
 * include burned calories or step calories in the GOAL denominator — those
 * are dynamic "extra allowance" that bumps the daily TARGET, not the macro
 * split percentages.
 */
import { computeCalorieAdjustment } from './nutritionCalculators';

export interface BodyProfile {
  /** Body weight in the user's preferred unit. */
  weight?: number | string | null;
  /** 'kg' (default) or 'lbs' / 'lb'. */
  weightUnit?: string | null;
  /** Height in the user's preferred unit. */
  height?: number | string | null;
  /** 'cm' (default) or 'in'. */
  heightUnit?: string | null;
  age?: number | string | null;
  /** 'male' (default) or 'female'. */
  gender?: string | null;
  /** 'maintain' (default) | 'lose' | 'lose_fast' | 'mini_cut' | 'gain' | 'gain_fast'. */
  weightGoal?: string | null;
  /** ISO date when the weight-goal block started (drives week-stepped calorie adjustment). */
  weightGoalStart?: string | null;
  /** Iter243: Mini-Cut duration in weeks (2 or 4). Ignored for non-mini_cut goals. */
  miniCutDurationWeeks?: 2 | 4 | null;
  /** Iter243: TDEE mode for Mini-Cut. When 'static', `overrideMaintenance` is
   *  ignored and baseTdee falls back to Mifflin × 1.2. Ignored for non-mini_cut
   *  goals. Defaults to 'adaptive'. */
  tdeeMode?: 'static' | 'adaptive' | null;
  /** Iter298: measured body fat % (manual entry, calculator or AI photo scan).
   *  Drives the LEAN-MASS protein dose. When absent an age/gender average is
   *  used and `proteinBasis` reports `lbm-estimated`. */
  bodyFatPct?: number | string | null;
}

/** Protein dose in grams per kg of LEAN body mass, by weight goal.
 *  Mini-cut is the most aggressive (short, steep deficit → maximum lean-mass
 *  protection); maintenance the lowest. */
export const PROTEIN_G_PER_KG_LBM: Record<string, number> = {
  mini_cut: 3.0,
  lose: 2.6,
  lose_fast: 2.6,
  maintain: 2.2,
  gain: 2.4,
  gain_fast: 2.4,
};

/** Fat as a share of daily calories. Bulking gets a lower share so the extra
 *  calories land in carbs (training fuel). */
const FAT_PCT: Record<string, number> = {
  gain: 0.25,
  gain_fast: 0.25,
};
const FAT_PCT_DEFAULT = 0.30;

/** Hormonal floor for fat, grams per kg BODY weight. */
const FAT_FLOOR_G_PER_KG = 0.6;

/** Population-average body fat % (NHANES-style bands) used only when the user
 *  has never entered / scanned a body fat reading. */
export function defaultBodyFatPct(age: number, gender: string): number {
  const isMale = gender === 'male';
  if (isMale) return age >= 50 ? 25 : age >= 30 ? 22 : 18;
  return age >= 50 ? 33 : age >= 30 ? 30 : 25;
}

export interface NutritionGoalsResult {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  /** The base TDEE used for the calculation (BMR × 1.2). Useful for displays
   *  that want to show the dynamic "allowance" including burned cals. */
  baseTdee: number;
  /** The signed calorie adjustment applied for this date (negative for lose,
   *  positive for gain). */
  calorieAdjustment: number;
  /** Iter298: `lbm` when a real body fat % was available, `lbm-estimated`
   *  when an age/gender average had to be used (UI shows a hint). */
  proteinBasis: 'lbm' | 'lbm-estimated';
  /** Lean body mass (kg) the protein dose was computed from. */
  leanMassKg: number;
  /** The body fat % actually used (measured or estimated). */
  bodyFatPctUsed: number;
}

const DEFAULT_GOALS: NutritionGoalsResult = {
  calories: 2000,
  protein: 150,
  carbs: 250,
  fat: 65,
  fiber: 25,
  baseTdee: 0,
  calorieAdjustment: 0,
  proteinBasis: 'lbm-estimated',
  leanMassKg: 0,
  bodyFatPctUsed: 0,
};

function toKg(weight: number, unit: string | null | undefined): number {
  return (unit === 'lbs' || unit === 'lb') ? weight * 0.453592 : weight;
}

function toCm(height: number, unit: string | null | undefined): number {
  return unit === 'in' ? height * 2.54 : height;
}

/** Compute the default daily nutrition goals for a user from their body
 *  profile, regardless of whether they've ever opened the goals editor.
 *
 *  Both the consumer Nutrition page AND the trainer's view of the same
 *  client MUST call this so the numbers match. Custom goal overrides (when
 *  the user explicitly edits one of calories/protein/carbs/fat/fiber)
 *  should be applied by the caller AFTER this returns — see
 *  `mergeWithCustomGoals` helper below.
 */
export function computeDailyNutritionGoals(
  profile: BodyProfile,
  /** ISO `YYYY-MM-DD` for date-aware adjustment (defaults to today). */
  dateKey?: string,
  /** Optional Adaptive TDEE maintenance override. When provided + > 0 it is
   *  used in place of `bmr × 1.2` so the macro split is computed against
   *  the user's measured maintenance instead of the static formula.
   *  Pass `null`/`undefined` to keep the legacy static behaviour. */
  overrideMaintenance?: number | null,
): NutritionGoalsResult {
  const weight = parseFloat(String(profile.weight ?? '0'));
  const height = parseFloat(String(profile.height ?? '0'));
  const age = parseInt(String(profile.age ?? '0'));
  const gender = String(profile.gender ?? 'male').toLowerCase();
  const weightGoal = String(profile.weightGoal ?? 'maintain').toLowerCase();

  const weightKg = toKg(weight, profile.weightUnit);
  const heightCm = toCm(height, profile.heightUnit);

  if (weightKg <= 0 || heightCm <= 0 || age <= 0) {
    return { ...DEFAULT_GOALS };
  }

  // Mifflin-St Jeor BMR (same as bmrUtils.ts).
  const bmr = gender === 'male'
    ? (10 * weightKg) + (6.25 * heightCm) - (5 * age) + 5
    : (10 * weightKg) + (6.25 * heightCm) - (5 * age) - 161;

  // Base TDEE — Adaptive TDEE override (measured maintenance) wins when
  // available, otherwise BMR × 1.2 sedentary multiplier (matches bmrUtils.ts).
  // Iter243: Mini-Cut respects an explicit tdeeMode='static' choice — the
  // user may want a stable Mifflin baseline for a short aggressive block
  // even when Adaptive TDEE has a valid estimate.
  const isMiniCutStatic = weightGoal === 'mini_cut' && profile.tdeeMode === 'static';
  const adaptiveValid = !isMiniCutStatic && overrideMaintenance != null && overrideMaintenance > 0;
  const baseTdee = adaptiveValid ? Math.round(overrideMaintenance as number) : Math.round(bmr * 1.2);

  // Iter226: delegate week/relax-day computation to the shared helper.
  // Fixes the timezone bug where `new Date('YYYY-MM-DD')` (UTC midnight)
  // + local `getDay()` slid the Sunday relax deficit onto Monday for
  // users west of UTC.
  const miniCutWeeks: 2 | 4 = profile.miniCutDurationWeeks === 4 ? 4 : 2;
  const { calorieAdjustment } = baseTdee > 0
    ? computeCalorieAdjustment(weightGoal, profile.weightGoalStart, dateKey, { baseTdee, miniCutWeeks })
    : { calorieAdjustment: 0 };

  // Target calories from base TDEE alone. Burned cals + step cals are NOT
  // included here — those are dynamic "extra allowance" shown separately
  // and must never affect the macro split percentages.
  const calories = Math.max(1200, baseTdee + calorieAdjustment);

  // Macros (iter298). Protein is dosed on LEAN body mass — a flat % of
  // calories over-doses a heavy/high-bodyfat user and under-doses a lean one,
  // and it made the target move whenever the calorie budget moved, which
  // contradicted the Body Info promise that protein follows lean mass.
  //   protein = g/kg LBM (by goal)   →  no cap (user choice)
  //   fat     = 30% of calories (25% when bulking), floor 0.6 g/kg bodyweight
  //   carbs   = EVERY remaining calorie
  const measuredBf = parseFloat(String(profile.bodyFatPct ?? ''));
  const hasMeasuredBf = Number.isFinite(measuredBf) && measuredBf >= 3 && measuredBf <= 70;
  const bodyFatPctUsed = hasMeasuredBf ? measuredBf : defaultBodyFatPct(age, gender);
  const leanMassKg = weightKg * (1 - bodyFatPctUsed / 100);

  const proteinPerKg = PROTEIN_G_PER_KG_LBM[weightGoal] ?? PROTEIN_G_PER_KG_LBM.maintain;
  const protein = Math.round(leanMassKg * proteinPerKg);
  const proteinKcal = protein * 4;

  const fatFloorKcal = weightKg * FAT_FLOOR_G_PER_KG * 9;
  const fatPct = FAT_PCT[weightGoal] ?? FAT_PCT_DEFAULT;
  let fatKcal = Math.max(calories * fatPct, fatFloorKcal);
  let carbsKcal = calories - proteinKcal - fatKcal;
  if (carbsKcal < 0) {
    // Very lean person in a steep deficit: give fat back down to its floor
    // before letting carbs go to zero.
    fatKcal = Math.max(fatFloorKcal, calories - proteinKcal);
    carbsKcal = Math.max(0, calories - proteinKcal - fatKcal);
  }

  // Fiber RDA from USDA / Institute of Medicine — same as consumer:
  //   Men <50: 38 g/day · Men 50+: 30 g/day
  //   Women <50: 25 g/day · Women 50+: 21 g/day
  const isMale = gender === 'male';
  const fiberGoal = isMale ? (age >= 50 ? 30 : 38) : (age >= 50 ? 21 : 25);

  return {
    calories: Math.round(calories),
    protein,
    carbs: Math.round(carbsKcal / 4),
    fat: Math.round(fatKcal / 9),
    fiber: fiberGoal,
    baseTdee,
    calorieAdjustment,
    proteinBasis: hasMeasuredBf ? 'lbm' : 'lbm-estimated',
    leanMassKg: Math.round(leanMassKg * 10) / 10,
    bodyFatPctUsed: Math.round(bodyFatPctUsed * 10) / 10,
  };
}

/** Apply user-edited custom goals on top of computed defaults. A null /
 *  undefined / 0 / NaN custom value falls back to the default. */
export function mergeWithCustomGoals(
  defaults: NutritionGoalsResult,
  custom: Partial<{ calories: number; protein: number; carbs: number; fat: number; fiber: number }> | null | undefined,
): NutritionGoalsResult {
  if (!custom) return defaults;
  const pick = (a: any, fallback: number): number => {
    const n = Number(a);
    return Number.isFinite(n) && n > 0 ? Math.round(n) : fallback;
  };
  return {
    ...defaults,
    calories: pick(custom.calories, defaults.calories),
    protein: pick(custom.protein, defaults.protein),
    carbs: pick(custom.carbs, defaults.carbs),
    fat: pick(custom.fat, defaults.fat),
    fiber: pick(custom.fiber, defaults.fiber),
  };
}
